import {
  Learnable,
  LearnableValue,
  LearnableValueKind,
  Screen,
  TemplateName,
} from 'src/api/learningSessions/types';
import { CardData } from 'src/apps/LearningSession/Common/learnableCards/utils';

/** One video the learner actually saw in the session that just finished. */
export interface ShownSessionVideo {
  src: string;
  actorId: number | null;
  actorName: string | null;
  accent: string | null;
}

export const MAX_SESSION_LOCALS = 5;

const videoOnScreen = (screen: Screen): LearnableValue | null => {
  if (screen.template === TemplateName.PRESENTATION) return screen.video;
  return screen.prompt.video;
};

const actorsForSrc = (learnable: Learnable, src: string) => {
  for (const screen of learnable.screens) {
    const video = videoOnScreen(screen);
    if (!video || video.kind !== LearnableValueKind.VIDEO || !Array.isArray(video.value)) continue;
    const index = (video.value as string[]).indexOf(src);
    if (index === -1) continue;
    return video.actors?.[index] ?? [];
  }
  return [];
};

export const shownVideoFromLearnable = (learnable: Learnable, src: string): ShownSessionVideo => {
  const actors = actorsForSrc(learnable, src);
  const actor = actors.find(item => item.name) ?? actors[0];
  return {
    src,
    actorId: actor?.id ?? null,
    actorName: actor?.name ?? null,
    accent: actor?.accent ?? null,
  };
};

export const videoSrcFromCard = (data: CardData): string | null => {
  if (data.kind === 'Presentation') return data.videoSrc;
  if (data.prompt.promptType === 'Video') return data.prompt.video;
  return null;
};

/** Still frame used as the portrait. Word videos have no separate thumbnail file. */
export const videoThumbnailSrc = (src: string): string =>
  src.includes('#') ? src : `${src}#t=0.1`;

export const rememberShownVideo = (
  videos: readonly ShownSessionVideo[],
  next: ShownSessionVideo,
): ShownSessionVideo[] => {
  if (!next.src || videos.length >= MAX_SESSION_LOCALS) return videos as ShownSessionVideo[];
  if (videos.some(video => video.src === next.src)) return videos as ShownSessionVideo[];
  if (next.actorId != null && videos.some(video => video.actorId === next.actorId)) {
    return videos as ShownSessionVideo[];
  }
  return [...videos, next];
};
